import { useState, useEffect } from "react";
import "./CRM.css";
import { useNavigate } from "react-router-dom";
import dataService from "../services/dataService";
import authService from "../services/authService";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { Eye, FileEdit, Trash2, Search, Plus, Download } from "lucide-react";

function CRMScreen() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // "view" or "edit"
  const [formData, setFormData] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [customerHistory, setCustomerHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await dataService.getCustomers();
      setCustomers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch customers", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (customers.length > 0) {
      const timer = setTimeout(() => {
        const needsFollowUp = customers.filter(c => c.tags && (c.tags.includes('Hot Lead') || c.tags.includes('Needs Follow-up')));
        if (needsFollowUp.length > 0) {
          console.log("Follow-up Toast Triggered for:", needsFollowUp.map(c => c.name));
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [customers]);

  const handleAddCustomer = () => {
    navigate("/customers/add");
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      
      const headers = rows[0].map(h => h.trim().replace(/"/g, ''));
      const nameIdx = headers.findIndex(h => h.toLowerCase().includes('name'));
      const companyIdx = headers.findIndex(h => h.toLowerCase().includes('company') || h.toLowerCase().includes('account'));
      const emailIdx = headers.findIndex(h => h.toLowerCase().includes('email'));
      const phoneIdx = headers.findIndex(h => h.toLowerCase().includes('phone'));
      const addressIdx = headers.findIndex(h => h.toLowerCase().includes('street') || h.toLowerCase().includes('address'));

      const newCustomers = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length < 2) continue;
        
        newCustomers.push({
          name: (row[nameIdx] || row[companyIdx] || 'Imported').replace(/"/g, '').trim(),
          companyName: (row[companyIdx] || '').replace(/"/g, '').trim(),
          email: (row[emailIdx] || '').replace(/"/g, '').trim(),
          phone: (row[phoneIdx] || '').replace(/"/g, '').trim(),
          address: (row[addressIdx] || '').replace(/"/g, '').trim(),
          status: 'Active',
          taxTreatment: 'VAT Registered'
        });
      }

      if (newCustomers.length > 0) {
        addToast(`Importing ${newCustomers.length} customers...`, "success");
        try {
          for (const cust of newCustomers) {
            await dataService.addCustomer(cust);
          }
          fetchCustomers();
          addToast("Import successful!", "success");
        } catch (err) {
          addToast("Error during import", "error");
        }
      }
    };
    reader.readAsText(file);
  };

  const fetchHistory = async (customerName) => {
    try {
      setHistoryLoading(true);
      const response = await dataService.getOrdersByCustomer(customerName);
      setCustomerHistory(response.data.data);
    } catch (error) {
      console.error("Failed to fetch customer history", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setModalMode("view");
    setIsModalOpen(true);
    fetchHistory(customer.name);
  };

  const handleEdit = (customer) => {
    navigate(`/customers/edit/${customer._id}`);
  };

  const handleDeleteClick = (customer) => {
    setItemToDelete(customer);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await dataService.deleteCustomer(itemToDelete._id);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchCustomers();
      addToast("Customer deleted successfully", "success");
    } catch (error) {
      addToast("Failed to delete customer: " + error.message, "error");
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dataService.updateCustomer(selectedCustomer._id, formData);
      setIsModalOpen(false);
      fetchCustomers();
      addToast("Customer updated successfully", "success");
    } catch (error) {
      addToast("Failed to update customer: " + error.message, "error");
    }
  };

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div className="header-info">
          <h1>Customer Relationship</h1>
          <p>Manage your customers and their orders</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="secondary-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={18} /> Import Zoho CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          </label>
          {authService.hasPermission('crm', 'editCustomers') && (
            <button className="primary-btn" onClick={handleAddCustomer}>
              <Plus size={18} /> Add Customer
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Customers</span>
            <h2 className="stat-value">{customers.length}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Active Now</span>
            <h2 className="stat-value">{customers.filter(c => c.status === 'Active').length}</h2>
          </div>
        </div>

      </div>

      <div className="content-card" style={{ marginTop: '32px' }}>
        <div className="card-controls">
          <h2 className="font-bold">Customers</h2>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search customers by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-wrapper">
          {loading ? (
            <p style={{ padding: '20px' }}>Loading customers...</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Company</th>
                  <th>Contact Info</th>
                  <th>Total Orders</th>
                  <th>Outstanding (AED)</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers
                  .filter(c =>
                    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    c.email.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((customer) => (
                    <tr key={customer._id}>
                      <td>
                        <div className="font-bold">{customer.name}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>ID: {customer._id.substring(0, 8)}</div>
                      </td>
                      <td>
                        <span style={{ fontSize: '13px', fontWeight: '500' }}>{customer.companyName || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>—</span>}</span>
                      </td>
                      <td>
                        <div className="contact-cell">
                          <span className="email">{customer.email}</span>
                          <span>{customer.phone}</span>
                          {customer.whatsapp && <span style={{ fontSize: '12px', color: '#059669' }}>WA: {customer.whatsapp}</span>}
                        </div>
                      </td>
                      <td>{customer.orders}</td>
                      <td>
                        <span className={`font-bold ${customer.outstandingBalance > 0 ? 'text-danger' : 'text-success'}`}>
                          {customer.outstandingBalance?.toLocaleString() || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status-pill ${customer.status.toLowerCase()}`}>
                          {customer.status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="action-icons">
                          <button className="icon-btn" title="View" onClick={() => handleView(customer)}>
                            <Eye size={16} />
                          </button>

                          {authService.hasPermission('crm', 'editCustomers') && (
                            <button className="icon-btn success" title="Edit" onClick={() => handleEdit(customer)}>
                              <FileEdit size={16} />
                            </button>
                          )}

                          {authService.hasPermission('crm', 'deleteCustomers') && (
                            <button className="icon-btn danger" title="Delete" onClick={() => handleDeleteClick(customer)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                {customers.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No customers found. Click "Add Customer" to get started.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalMode === "view" ? "Customer Details" : "Edit Customer"}
        footer={
          modalMode === "edit" ? (
            <>
              <button className="secondary-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="primary-btn" onClick={handleUpdateSubmit}>Save Changes</button>
            </>
          ) : (
            <button className="primary-btn" onClick={() => setIsModalOpen(false)}>Close</button>
          )
        }
      >
        {modalMode === "view" && selectedCustomer && (
          <div className="detail-view">
            <div className="detail-item">
              <span className="detail-label">Full Name</span>
              <span className="detail-value">{selectedCustomer.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Company Name</span>
              <span className="detail-value">{selectedCustomer.companyName || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Subject</span>
              <span className="detail-value">{selectedCustomer.subject || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Email Address</span>
              <span className="detail-value">{selectedCustomer.email}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Phone Number</span>
              <span className="detail-value">{selectedCustomer.phone}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">WhatsApp</span>
              <span className="detail-value">{selectedCustomer.whatsapp || "N/A"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Address</span>
              <span className="detail-value">{selectedCustomer.address}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Tax Treatment</span>
              <span className="detail-value">{selectedCustomer.taxTreatment || "VAT Registered"}</span>
            </div>
            {selectedCustomer.taxTreatment === "VAT Registered" && (
              <div className="detail-item">
                <span className="detail-label">TRN</span>
                <span className="detail-value">{selectedCustomer.trn || "N/A"}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">Place of Supply</span>
              <span className="detail-value">{selectedCustomer.placeOfSupply || "Dubai"}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <span className={`status-pill ${selectedCustomer.status?.toLowerCase()}`}>
                {selectedCustomer.status}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Tags</span>
              <div className="tags-display" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                {selectedCustomer.tags && selectedCustomer.tags.length > 0 ? (
                  selectedCustomer.tags.map((tag, idx) => (
                    <span key={idx} style={{ background: '#EEF2FF', color: '#4F46E5', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', border: '1px solid #C7D2FE' }}>
                      {tag}
                    </span>
                  ))
                ) : (
                  <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No tags assigned</span>
                )}
              </div>
            </div>

            <div className="history-section" style={{ marginTop: '24px' }}>
              <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>Transaction History</h3>
              {historyLoading ? (
                <p style={{ fontSize: '14px', color: '#6B7280' }}>Loading history...</p>
              ) : customerHistory.length > 0 ? (
                <div className="table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  <table className="data-table" style={{ fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerHistory.map(order => (
                        <tr key={order._id}>
                          <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                          <td>{order.type}</td>
                          <td>{order.total}</td>
                          <td>
                            <span className={`status-pill ${order.status?.toLowerCase()}`} style={{ padding: '2px 8px', fontSize: '11px' }}>
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ fontSize: '14px', color: '#6B7280' }}>No recent transactions found.</p>
              )}
            </div>
          </div>
        )
        }

        {
          modalMode === "edit" && (
            <form className="modal-form" onSubmit={handleUpdateSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input name="name" value={formData.name || ""} onChange={handleFormChange} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input name="email" value={formData.email || ""} onChange={handleFormChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input name="phone" value={formData.phone || ""} onChange={handleFormChange} />
              </div>
              <div className="form-group">
                <label>WhatsApp</label>
                <input name="whatsapp" value={formData.whatsapp || ""} onChange={handleFormChange} />
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input name="companyName" value={formData.companyName || ""} onChange={handleFormChange} placeholder="Enter company name" />
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea name="address" value={formData.address || ""} onChange={handleFormChange}></textarea>
              </div>
              <div className="form-group">
                <label>Subject</label>
                <input name="subject" value={formData.subject || ""} onChange={handleFormChange} placeholder="Enter subject/inquiry" />
              </div>
              <div className="form-group">
                <label>Tax Treatment</label>
                <select name="taxTreatment" value={formData.taxTreatment || "VAT Registered"} onChange={handleFormChange}>
                  <option value="VAT Registered">VAT Registered</option>
                  <option value="Non VAT Registered">Non VAT Registered</option>
                </select>
              </div>
              {formData.taxTreatment === "VAT Registered" && (
                <div className="form-group">
                  <label>TRN</label>
                  <input name="trn" value={formData.trn || ""} onChange={handleFormChange} placeholder="Enter TRN" />
                </div>
              )}
              <div className="form-group">
                <label>Place of Supply</label>
                <select name="placeOfSupply" value={formData.placeOfSupply || "Dubai"} onChange={handleFormChange}>
                  <option value="Dubai">Dubai</option>
                  <option value="Abu Dhabi">Abu Dhabi</option>
                  <option value="Sharjah">Sharjah</option>
                  <option value="Ajman">Ajman</option>
                  <option value="Umm Al Quwain">Umm Al Quwain</option>
                  <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                  <option value="Fujairah">Fujairah</option>
                  <option value="Outside UAE">Outside UAE</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status || "Active"} onChange={handleFormChange}>
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  name="tags"
                  value={formData.tags ? formData.tags.join(', ') : ""}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) })}
                  placeholder="e.g. VIP, Hot Lead, Needs Follow-up"
                />
              </div>
            </form>
          )
        }
      </Modal >

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        footer={
          <>
            <button className="secondary-btn" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
            <button className="primary-btn btn-danger" onClick={confirmDelete}>Delete Customer</button>
          </>
        }
      >
        <div className="delete-confirm-view">
          <div className="delete-icon-wrapper">
            <Trash2 size={32} />
          </div>
          <h3>Are you sure?</h3>
          <p>You are about to delete <strong>{itemToDelete?.name}</strong>. This action cannot be undone and will remove the customer and their history.</p>
        </div>
      </Modal>
    </div >
  );
}

export default CRMScreen;